<template>
	<v-dialog
		:model-value="dialogVisible"
		:width="$vuetify.display.smAndDown ? '95%' : '500px'"
		class="sgnl-mx-0 sgnl-px-0"
		persistent
	>
		<v-card
			class="validation-card"
			:class="{ 'validation-card--error': showError }"
		>
			<div
				class="sgnl-h5-medium"
				:class="titleClass"
			>
				Connecting to repository
			</div>
			<div
				class="sgnl-paragraph-m-regular"
				:class="titleClass"
			>
				{{ fullNameOfRepoToConnect }}
			</div>
			<div>
				<v-slide-y-transition
					tag="div"
					class="sgnl-py-0"
					group
				>
					<template
						v-for="(authTask, i) in authTasks"
						:key="`${i}-${authTask.text}`"
					>
						<v-list-item>
							<div
								class="sgnl-paragraph-s-regular"
								:class="authTaskStatusClass(authTask.status)"
								v-text="authTask.text"
							/>
							<v-spacer />
							<v-scale-transition hide-on-leave>
								<Checkmark16
									v-if="authTask.status === TaskStatusE.Success"
									:class="authTaskStatusClass(authTask.status)"
								/>
								<Close16
									v-else-if="authTask.status == TaskStatusE.Fail"
									:class="authTaskStatusClass(authTask.status)"
								/>
								<SubtractAlt16
									v-else-if="authTask.status == TaskStatusE.Ignored"
									:class="authTaskStatusClass(authTask.status)"
								/>
								<v-progress-circular
									v-else
									:width="3"
									:size="25"
									color="var(--sgnl-brand-background)"
								/>
							</v-scale-transition>
						</v-list-item>
					</template>
				</v-slide-y-transition>
			</div>
			<v-expand-transition>
				<AlertMessage
					:show-alert="showError"
					type="error"
				>
					{{ errorMessage }}
				</AlertMessage>
			</v-expand-transition>
			<v-expand-transition>
				<v-card-actions
					v-if="showError"
					class="sgnl-flex sgnl-p-0 sgnl-gap-12"
				>
					<v-btn
						v-if="!githubStore.githubLoggedIn"
						variant="flat"
						color="primary"
						class="github-login-btn"
						:loading="forceAddLoading"
						@click="githubLogin"
					>
						<LogoGithub16 />
						GitHub Login
					</v-btn>
					<v-btn
						variant="outlined"
						@click="closeDialog"
					>
						Close
					</v-btn>
				</v-card-actions>
			</v-expand-transition>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

// Utilities
import { getRepoData, mockTimeout, initiateGithubLoginRepositoryRetry } from "@/js/githubClient";
import { signaloidApiClient } from "@/js/signaloidClient";

import { monitoringCaptureError } from "@/plugins/monitoring";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { generateGithubResponseErrorMessage } from "@/js/githubUtilities";

// Libraries
import axios from "axios";

// Stores
import { useRepositoriesStore } from "@/stores/repositories";
import { useGithubStore } from "@/stores/github";
import { useRootStore } from "@/stores/root";
import { useCoresStore } from "@/stores/cores";

// Components
import AlertMessage from "@/components/Common/AlertMessage.vue";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import SubtractAlt16 from "@carbon/icons-vue/es/subtract--alt/16";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";

// Types
import { RepoConnectionConfig } from "@/types/general";
import type { Endpoints } from "@octokit/types";
import { RepositoryConnectionRequestConfig } from "@/js/signaloidClient.types";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { UserLimitsE } from "@/js/tierconfig";

export const TaskE = {
	CheckRepoDoesntExist: "sig_checkExisting",
	ConnectToGithub: "gh_comm",
	CheckForSignaloidConfig: "sig_conf",
	CheckSrcPathExists: "sig_srcPath",
	CheckInputsPathExists: "sig_inputsPath",
	AddRepoToAccount: "sig_connectToRepo",
	RedirectToRepoPage: "sig_redirect",
} as const;
export type Task = (typeof TaskE)[keyof typeof TaskE];

export const TaskStatusE = {
	InProgress: "In Progress",
	Success: "Success",
	Fail: "Fail",
	Ignored: "Ignored",
	Error: "Error",
} as const;
export type TaskStatus = (typeof TaskStatusE)[keyof typeof TaskStatusE];

type RepoContents = Endpoints["GET /repos/{owner}/{repo}"]["response"]["data"];

type AuthTask = {
	task: Task;
	status: TaskStatus;
	text: string;
};
type ComponentData = {
	showError: boolean;
	errorMessage: string;
	authTasks: AuthTask[];
	rootContents: undefined | RepoContents;
	forceAddLoading: boolean;
	isTierRestrictionError: boolean;
	defaultBranch: string;
};
export default defineComponent({
	name: "RepositoryConnectionValidationCard",
	components: { AlertMessage, Checkmark16, Close16, SubtractAlt16, LogoGithub16 },
	props: {
		connectionConfig: { type: Object as PropType<RepoConnectionConfig>, required: true },
		dialogVisible: { type: Boolean },
	},
	emits: {
		"close-dialog": () => true,
		"repository-connected": (_data: RepoConnectionConfig, _alreadyConnected: boolean) => true,
	},
	data: (): ComponentData => ({
		showError: false,
		errorMessage: "",
		authTasks: [],
		rootContents: undefined,
		forceAddLoading: false,
		isTierRestrictionError: false,
		defaultBranch: "",
	}),
	setup() {
		const repositoriesStore = useRepositoriesStore();
		const githubStore = useGithubStore();
		const rootStore = useRootStore();
		const coresStore = useCoresStore();
		return { repositoriesStore, githubStore, rootStore, coresStore, TaskStatusE };
	},
	computed: {
		fullNameOfRepoToConnect(): string {
			return this.connectionConfig.remoteURL.pathname.replace(/^\/|\/$/g, "").replace(/\.git$/, "");
		},
		titleClass(): string {
			return this.showError ? "sgnl-text-color-error" : "sgnl-text-color-brand";
		},
	},
	methods: {
		authTaskStatusClass(taskStatus: TaskStatus): string {
			switch (taskStatus) {
				case TaskStatusE.InProgress:
					return "sgnl-text-color-brand";
				case TaskStatusE.Success:
					return "sgnl-text-color-brand";
				case TaskStatusE.Fail:
					return "sgnl-text-color-error";
				case TaskStatusE.Ignored:
					return "sgnl-text-color-secondary";
				default:
					return "sgnl-text-color-error";
			}
		},
		async getRootContents() {
			try {
				const response = await signaloidApiClient.get<RepoContents>(
					`proxy/github/repos/${this.fullNameOfRepoToConnect}/contents`,
				);
				this.rootContents = response.data;
			} catch (error) {
				monitoringCaptureError(error, "Connect to repository: get contents");
				if (axios.isAxiosError(error)) {
					this.errorMessage = generateGithubResponseErrorMessage(error, this.githubStore.githubLoggedIn);
				}
			}
		},
		githubLogin() {
			//@ts-ignore
			this.$posthog?.capture("github_login_initiated");

			mockTimeout(500).then(() => {
				initiateGithubLoginRepositoryRetry(this.connectionConfig.remoteURL.toString());
			});
		},
		async getDefaultBranch() {
			try {
				const response = await getRepoData(this.fullNameOfRepoToConnect);
				return response.data.default_branch;
			} catch {
				return "master";
			}
		},
		async validConfigFile() {
			// let configIndex = this.rootContents.data.findIndex((x) => x.name === "signaloid.yaml");
			// return configIndex >= 0 ? true : false;
			return false;
		},
		async validSrcDirectory(srcPath) {
			if (!this.rootContents || !Array.isArray(this.rootContents)) {
				return false;
			}

			const srcIndex = this.rootContents.findIndex((x) => x.name === srcPath);
			return srcIndex >= 0 ? true : false;
		},
		async validInputsDirectory(inputsPath) {
			if (!this.rootContents || !Array.isArray(this.rootContents)) {
				return false;
			}

			const inputsIndex = this.rootContents.findIndex((x) => x.name === inputsPath);
			return inputsIndex >= 0 ? true : false;
		},
		async checkCSourceFiles(srcPath) {
			let srcContents;
			let cFileList = [];
			try {
				srcContents = await signaloidApiClient.get(
					`/proxy/github/repos/${this.fullNameOfRepoToConnect}/contents/${srcPath}`,
				);
				cFileList = srcContents.data.filter((x) => {
					const fName = x.name.toLowerCase();
					return (
						fName.includes(".c") ||
						fName.includes(".cc") ||
						fName.includes(".cp") ||
						fName.includes(".cpp") ||
						fName.includes(".c++") ||
						fName.includes(".cxx")
					);
				});
			} catch (error) {
				monitoringCaptureError(error, "Connect to repository: check for source files");
			}

			return cFileList.length > 0 ? true : false;
		},
		setTaskStatus(taskName: Task, status: TaskStatus) {
			this.authTasks[this.authTasks.findIndex((x) => x.task === taskName)].status = status;
		},
		retryValidation() {
			this.authTasks = [];
			this.showError = false;
			this.errorMessage = "";
			this.validateRepositoryAndConnect();
		},
		async validateRepositoryAndConnect() {
			let alreadyConnected = false;
			try {
				this.defaultBranch = await this.getDefaultBranch();

				this.authTasks.push({
					task: TaskE.ConnectToGithub,
					status: TaskStatusE.InProgress,
					text: "Communicating with GitHub...",
				});

				await this.getRootContents();

				if (!this.rootContents) {
					this.setTaskStatus(TaskE.ConnectToGithub, TaskStatusE.Fail);
					throw new Error(this.errorMessage);
				}
				this.setTaskStatus(TaskE.ConnectToGithub, TaskStatusE.Success);

				this.authTasks.push({
					task: TaskE.CheckForSignaloidConfig,
					status: TaskStatusE.InProgress,
					text: "Looking for Signaloid configuration file...",
				});

				const srcPath = "src";
				const inputsPath = "inputs";

				const configValid = await this.validConfigFile();
				if (configValid) {
					this.setTaskStatus(TaskE.CheckForSignaloidConfig, TaskStatusE.Success);
				} else {
					this.setTaskStatus(TaskE.CheckForSignaloidConfig, TaskStatusE.Ignored);
				}

				this.authTasks.push({
					task: TaskE.CheckSrcPathExists,
					status: TaskStatusE.InProgress,
					text: `Looking for './${srcPath}' directory...`,
				});

				this.authTasks.push({
					task: TaskE.CheckInputsPathExists,
					status: TaskStatusE.InProgress,
					text: `Looking for input files in './${inputsPath}'...`,
				});

				const inputFilesExist = await this.validInputsDirectory(inputsPath);
				if (inputFilesExist) {
					this.setTaskStatus(TaskE.CheckInputsPathExists, TaskStatusE.Success);
				} else {
					this.setTaskStatus(TaskE.CheckInputsPathExists, TaskStatusE.Ignored);
				}

				const srcExists = await this.validSrcDirectory(srcPath);
				if (!srcExists) {
					this.setTaskStatus(TaskE.CheckSrcPathExists, TaskStatusE.Fail);
					this.errorMessage = "There is no './src' directory in the repository.";
					throw new Error(this.errorMessage);
				}
				this.setTaskStatus(TaskE.CheckSrcPathExists, TaskStatusE.Success);

				this.authTasks.push({
					task: TaskE.AddRepoToAccount,
					status: TaskStatusE.InProgress,
					text: `Adding to your Signaloid Cloud Account...`,
				});

				const connectResult = await this.connectToRepository(this.defaultBranch);
				if (connectResult === "already-connected") {
					alreadyConnected = true;
					this.setTaskStatus(TaskE.AddRepoToAccount, TaskStatusE.Success);
				} else if (connectResult === "connected") {
					this.setTaskStatus(TaskE.AddRepoToAccount, TaskStatusE.Success);
				} else {
					this.setTaskStatus(TaskE.AddRepoToAccount, TaskStatusE.Fail);
					throw new Error(this.errorMessage);
				}
			} catch (error) {
				if (axios.isAxiosError(error)) {
					if (error.response) {
						//non 2xx response
						this.errorMessage = `A network error occurred while connecting the repository - ${error.response}. Please try again.`;
					} else {
						this.errorMessage = `A browser error occurred while connecting the repository - Request Failed. Please check your network connection and try again.`;
					}
				} else {
					// this.errorMessage = String(error.message);
				}
				monitoringCaptureError(error, "Connect to repository");

				this.showError = true;
			} finally {
				if (!this.showError) {
					this.authTasks.push({
						task: TaskE.RedirectToRepoPage,
						status: TaskStatusE.InProgress,
						text: "redirecting...",
					});
					mockTimeout(1000).then(() => {
						this.$emit("repository-connected", this.connectionConfig, alreadyConnected);
						this.closeDialog();
					});
				}
			}
		},
		async forceAddRepository() {
			this.authTasks.push({
				task: TaskE.AddRepoToAccount,
				status: TaskStatusE.InProgress,
				text: `Forcefully adding to your Signaloid Cloud Account...`,
			});
			this.showError = false;
			this.forceAddLoading = true;
			mockTimeout(1000).then(async () => {
				const branch = this.defaultBranch || (await this.getDefaultBranch());
				const result = await this.connectToRepository(branch);
				if (result === "connected" || result === "already-connected") {
					this.$emit("repository-connected", this.connectionConfig, result === "already-connected");
				}
				this.closeDialog();
			});
		},
		async connectToRepository(branch: string): Promise<"connected" | "already-connected" | "failed"> {
			const repoConfig: RepositoryConnectionRequestConfig = {
				RemoteURL: this.connectionConfig.remoteURL.href,
				Commit: "HEAD",
				BuildDirectory: "src",
				Arguments: "",
				Branch: branch,
				Core: this.coresStore.defaultCoreID,
			};

			try {
				const response = await this.repositoriesStore.connectToRepository(repoConfig);
				const defaultCoreID = this.coresStore.defaultCoreID;
				if (defaultCoreID && response.Core !== defaultCoreID) {
					await this.repositoriesStore.updateRepositoryConfig(response.RepositoryID, { Core: defaultCoreID });
				}
				this.rootStore.tierLimitEventBus.emit({
					type: TierLimitEventTypeE.UsageChanged,
					affectedLimits: [UserLimitsE.RepositoryCount],
				});
				return "connected";
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_CONFLICT) {
					return "already-connected";
				}

				monitoringCaptureError(error, "Connect to repository");

				if (error instanceof SdkError) {
					if (error.code === ERROR_CODES.API_FORBIDDEN) {
						this.isTierRestrictionError = true;
						this.rootStore.tierLimitEventBus.emit({
							type: TierLimitEventTypeE.LimitExceeded,
							affectedLimits: [UserLimitsE.RepositoryCount],
						});
						this.errorMessage = "Connected repositories limit reached.";
					} else {
						this.errorMessage = "Could not connect to Signaloid Cloud.";
					}
				} else if (axios.isAxiosError(error)) {
					// SDK forwards raw axios errors when there is no response
					// (network down, DNS failure).
					this.errorMessage =
						"Failed to connect the repository. Make sure you have an active internet connection.";
				} else {
					this.errorMessage = "Could not connect to Signaloid Cloud. An unexpected error occurred.";
				}
				this.setTaskStatus(TaskE.AddRepoToAccount, TaskStatusE.Fail);
			}
			return "failed";
		},
		closeDialog() {
			this.$emit("close-dialog");
		},
	},
	async mounted() {
		await this.validateRepositoryAndConnect();
	},
});
</script>

<style>
.validation-card.v-card {
	padding: var(--sgnl-space-16);
	gap: var(--sgnl-space-16);
	display: flex;
	flex-direction: column;
	background-color: var(--sgnl-brand-background-inverse);
}

.validation-card--error.v-card {
	background-color: var(--sgnl-alert-error-background-primary);
}

.validation-card .v-list-item {
	padding-inline: 0;
	min-height: auto;
}

.github-login-btn .v-btn__content {
	gap: var(--sgnl-space-4);
}
</style>
